import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {Row} from "reactstrap";
import Dialog from "../../base/dialog";
import TopBarProgressIndicator from "../../app-specific/topbar-progress-indicator";
import useIsMounted from "../../../hooks/use-is-mounted";
import AddNCRBasicInfoSection from "./sections/basic-info";
import AddNCRQuestionsSection from "./sections/questions";
import AddNCRTargetSection, {NCRTargetsTabs} from "./sections/target";
import Api from "../../../../core/services/api/api";
import DateUtils from "../../../../core/services/date-utils";
import {useOperatorInformation} from "../../../hooks/use-operator-information";

const initBasicInfoValues = {
    manualNcr: false,
    ncr: undefined,
    reportedBy: undefined,
    // assignedTo: undefined,
    rgaNumber: undefined,
    submissionDate: undefined,
    todaySubmission: true,
    description: undefined,
};

const initTargetLists = {
    processes: [],
    products: [],
}


export const UpsertNCRProcessSteps = {
    basicInfo: 'Basic Information',
    questions: 'Questions',
    target: 'Targets',
}

const UpsertNCRDialog = ({
                             open,
                             setOpen,
                             onSuccess,
                             report,
                             process = UpsertNCRProcessSteps.basicInfo,
                             targetsTab = NCRTargetsTabs.products,
                             disableNavigatingBetweenSteps = false,
                             formId: formIdProps,
                             formResponseId: formResponseIdProps,
                         }) => {
    const [currentProcess, setCurrentProcess] = useState(process ?? UpsertNCRProcessSteps.basicInfo);
    const [basicInfoValues, setBasicInfoValues] = useState(initBasicInfoValues);
    const [targetLists, setTargetLists] = useState(initTargetLists);
    const [formId, setFormId] = useState(formIdProps);
    const [formResponseId, setFormResponseId] = useState(formResponseIdProps);
    const [submittingReport, setSubmittingReport] = useState(false);
    const [submittedReport, setSubmittedReport] = useState(null);
    const isMounted = useIsMounted();

    const operatorInformation = useOperatorInformation();
    const createInitialReport = useMemo(() => !report, [report]);

    /**
     * With each change in the open state of the dialog and formToken props:
     * - if dialog is open, sync the formResponseToken state value with the given formTokenProps;
     */
    useLayoutEffect(() => {
        if (open) {
            setFormId(formIdProps);
            setFormResponseId(formResponseIdProps);
        }
    }, [open, formIdProps, formResponseIdProps])

    /**
     * With each change in the open state of the dialog and reportId props:
     * - if dialog is open, sync the submittedReport state value with the given reportId;
     */
    useEffect(() => {
        const submittedReport = report ? {
            id: report.id,
            code: report.code,
            statusId: report?.statusId,
            statuses: report.statuses,
            shortDescription: report.shortDescription,
            submitDateTime: report.submitDateTime,
            reportedDateTime: report.reportedDateTime,
            createdBy: report.createdByOperator?.id,
            createdByOperatorFirstName: report.createdByOperator?.firstname,
            createdByOperatorLastName: report.createdByOperator?.lastname,
            reportedBy: report.reportedByOperator?.id,
            reportedByOperatorFirstName: report.reportedByOperator?.firstname,
            reportedByOperatorLastName: report.reportedByOperator?.lastname,
            // assignedTo: report.assignedToOperator?.id,
            // assignedToOperatorFirstName: report.assignedToOperator?.firstname,
            // assignedToOperatorLastName: report.assignedToOperator?.lastname,
            processes: report.processes,
            parts: report.parts,
            rgaNumber: report.rgaNumber,
        } : null
        if (open && report) {
            setSubmittedReport(submittedReport)
        }
        setBasicInfoValues({
            manualNcr: !!submittedReport,
            ncr: submittedReport?.code,
            rgaNumber: submittedReport?.rgaNumber,
            reportedBy: submittedReport?.reportedBy
                ? {
                    id: submittedReport?.reportedBy,
                    firstName: submittedReport?.reportedByOperatorFirstName,
                    lastName: submittedReport?.reportedByOperatorLastName,
                }
                : undefined,
            // assignedTo: submittedReport?.assignedTo
            //     ? {
            //         id: submittedReport?.assignedTo,
            //         firstName: submittedReport?.assignedToOperatorFirstName,
            //         lastName: submittedReport?.assignedToOperatorLastName,
            //     }
            //     : undefined,
            submissionDate: submittedReport?.reportedDateTime,
            todaySubmission: !submittedReport?.reportedDateTime,
            description: submittedReport?.shortDescription,
        })
        setTargetLists({
            processes: submittedReport?.processes ?? [],
            products: submittedReport?.parts ?? [],
        })
    }, [open, report])

    /**
     * With each change in the open state of the dialog and process props:
     * - if dialog is open, sync the current process's state value with the given processProps;
     */
    useEffect(() => {
        if (open) {
            setCurrentProcess(process ?? UpsertNCRProcessSteps.basicInfo);
        }
    }, [process, open])

    /**
     * Resets the state of the modal as soon as the process successful finishes and calls onSuccess with the
     * returned request.
     * @param createdReport {any}
     */
    const terminateProcess = useCallback((reportInfo = null, formResponseReference = null) => {
        if ((reportInfo || targetLists) && onSuccess) {
            onSuccess?.({
                ...reportInfo,
                id: reportInfo?.id,
                code: reportInfo?.code,
                statuses: reportInfo?.statuses,
                shortDescription: reportInfo?.shortDescription,
                submitDateTime: reportInfo?.submitDateTime,
                reportedDateTime: reportInfo?.reportedDateTime,
                createdByOperator: {
                    id: reportInfo?.createdBy,
                    firstname: reportInfo?.createdByOperatorFirstName,
                    lastname: reportInfo?.createdByOperatorLastName,
                },
                reportedByOperator: {
                    id: reportInfo?.reportedBy,
                    firstname: reportInfo?.reportedByOperatorFirstName,
                    lastname: reportInfo?.reportedByOperatorLastName,
                },
                // assignedToOperator: {
                //     id: reportInfo?.assignedTo,
                //     firstname: reportInfo?.assignedToOperatorFirstName,
                //     lastname: reportInfo?.assignedToOperatorLastName,
                // },
                processes: targetLists.processes,
                parts: targetLists.products,
            }, formId, formResponseReference ?? formResponseId);
        }
        setCurrentProcess(UpsertNCRProcessSteps.basicInfo);
        setBasicInfoValues(initBasicInfoValues);
        setTargetLists(initTargetLists);
        setSubmittedReport(null);
        setOpen(false);
    }, [formId, formResponseId, onSuccess, setOpen, targetLists]);

    /**
     * With each change in the open state of the dialog and currentProcess props:
     * - if dialog is open, sync the current process's state value with the given currentProcessProps;
     */
    useLayoutEffect(() => {
        if (!open && submittedReport) {
            terminateProcess(submittedReport);
        }
    }, [open]);

    /**
     * Submits the basic info form and then updates the ncr report
     * @type {(function(*): Promise<void>)}
     */
    const submitBasicInfo = useCallback(async (values) => {
        setBasicInfoValues(values);
        if (submittedReport) {
            // update
            const forApi = {
                id: submittedReport.id,
                code: values.manualNcr ? values.ncr : null,
                rgaNumber: values.rgaNumber,
                shortDescription: values.description,
                reportedDateTime: !values.todaySubmission ? values.submissionDate : DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
                reportedBy: {
                    id: values.reportedBy?.id,
                    firstname: values.reportedBy?.firstName ?? values.reportedBy?.firstname,
                    lastname: values.reportedBy?.lastName ?? values.reportedBy?.lastname,
                },
                // assignedTo: {
                //     id: values.assignedTo?.id,
                //     firstname: values.assignedTo?.firstName ?? values.assignedTo?.firstname,
                //     lastname: values.assignedTo?.lastName ?? values.assignedTo?.lastname,
                // },
                updatedProperties: [],
            }
            if (submittedReport.code !== forApi.code) {
                forApi.updatedProperties.push("Code");
            }
            if (submittedReport.shortDescription !== forApi.shortDescription) {
                forApi.updatedProperties.push("ShortDescription");
            }
            if (submittedReport.reportedDateTime !== forApi.reportedDateTime) {
                forApi.updatedProperties.push("ReportedDateTime");
            }
            if (submittedReport.reportedBy !== forApi.reportedBy.id) {
                forApi.updatedProperties.push("ReportedBy");
            }
            // if (submittedReport.assignedTo !== forApi.assignedTo.id) {
            //     forApi.updatedProperties.push("AssignedTo");
            // }
            if (submittedReport.rgaNumber !== forApi.rgaNumber) {
                forApi.updatedProperties.push("RgaNumber");
            }
            const response = await Api.updateNcrReport(forApi);
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                if (disableNavigatingBetweenSteps) {
                    terminateProcess({
                        ...(submittedReport ?? {}),
                        code: values.manualNcr ? values.ncr : null,
                        rgaNumber: forApi.rgaNumber,
                        reportedBy: forApi.reportedBy.id,
                        reportedByOperatorFirstName: forApi.reportedBy.firstname,
                        reportedByOperatorLastName: forApi.reportedBy.lastname,
                        // assignedTo: forApi.assignedTo.id,
                        // assignedToOperatorFirstName: forApi.assignedTo.firstname,
                        // assignedToOperatorLastName: forApi.assignedTo.lastname,
                        reportedDateTime: forApi.reportedDateTime,
                        shortDescription: forApi.shortDescription,
                    });
                } else {
                    setCurrentProcess(UpsertNCRProcessSteps.questions);
                }
            }
        } else {
            // in create process so do nothing
            setCurrentProcess(UpsertNCRProcessSteps.questions);
        }
    }, [isMounted, submittedReport, disableNavigatingBetweenSteps, terminateProcess]);

    /**
     * Submits the report form and then the ncr report
     * @type {(function(*, *): Promise<void>)}
     */
    const initiateReport = useCallback(async (formResponseReference) => {
        setFormResponseId(formResponseReference);
        setSubmittingReport(true);
        if (createInitialReport) {
            const response = await Api.initiateNcrReport({
                code: basicInfoValues.manualNcr ? basicInfoValues.ncr : null,
                shortDescription: basicInfoValues.description,
                reportedDateTime: !basicInfoValues.todaySubmission ? basicInfoValues.submissionDate : null,
                reportedBy: basicInfoValues.reportedBy?.id === operatorInformation?.id
                    ? null
                    : {
                        id: basicInfoValues.reportedBy?.id,
                        firstname: basicInfoValues.reportedBy?.firstName ?? basicInfoValues.reportedBy?.firstname,
                        lastname: basicInfoValues.reportedBy?.lastName ?? basicInfoValues.reportedBy?.lastname,
                    },
                // assignedTo: {
                //     id: basicInfoValues.assignedTo?.id,
                //     firstname: basicInfoValues.assignedTo?.firstName ?? basicInfoValues.assignedTo?.firstname,
                //     lastname: basicInfoValues.assignedTo?.lastName ?? basicInfoValues.assignedTo?.lastname,
                // },
                formResponseReference: formResponseReference,
            });
            if (!isMounted())
                return;
            setSubmittingReport(false);
            if (response?.resultFlag) {
                const report = {
                    id: response.data.id,
                    code: response.data.code,
                    statusId: response.data?.statusId,
                    shortDescription: response.data.shortDescription,
                    submitDateTime: response.data.submitDateTime,
                    reportedDateTime: response.data.reportedDateTime,
                    createdBy: operatorInformation?.id,
                    createdByOperatorFirstName: operatorInformation?.firstName ?? "",
                    createdByOperatorLastName: operatorInformation?.lastName ?? "",
                    reportedBy: basicInfoValues.reportedBy?.id,
                    reportedByOperatorFirstName: basicInfoValues.reportedBy?.firstName ?? basicInfoValues.reportedBy?.firstname,
                    reportedByOperatorLastName: basicInfoValues.reportedBy?.lastName ?? basicInfoValues.reportedBy?.lastname,
                    // assignedTo: basicInfoValues.assignedTo?.id,
                    // assignedToOperatorFirstName: basicInfoValues.assignedTo?.firstName ?? basicInfoValues.assignedTo?.firstname,
                    // assignedToOperatorLastName: basicInfoValues.assignedTo?.lastName ?? basicInfoValues.assignedTo?.lastname,
                    processes: [],
                    parts: [],
                }
                setSubmittedReport(report);
                if (disableNavigatingBetweenSteps) {
                    terminateProcess(report, formResponseReference)
                } else {
                    setCurrentProcess(UpsertNCRProcessSteps.target);
                }
            } else if (!formResponseReference) {
                setCurrentProcess(UpsertNCRProcessSteps.basicInfo);
            }
        } else {
            if (!submittedReport?.statusId || (!formResponseReference && (submittedReport?.statuses?.find(e => e.isCurrentStatus)?.statusId) === submittedReport?.statusId)) {
                setSubmittingReport(false);
                const report = await new Promise((r) => {
                    setSubmittedReport((prevState) => {
                        r(prevState);
                        return prevState;
                    });
                })
                if (disableNavigatingBetweenSteps) {
                    terminateProcess(report, formResponseReference)
                } else {
                    setCurrentProcess(UpsertNCRProcessSteps.target);
                }
                return;
            }
            const response = await Api.changeNcrReportStatus({
                id: submittedReport?.id,
                statusId: submittedReport?.statusId,
                formResponseReference: formResponseReference,
            });
            if (!isMounted())
                return;
            setSubmittingReport(false);
            if (response?.resultFlag) {
                const report = await new Promise((r) => {
                    setSubmittedReport((prevState) => {
                        r(prevState);
                        return prevState;
                    });
                })
                if (disableNavigatingBetweenSteps) {
                    terminateProcess(report, formResponseReference)
                } else {
                    setCurrentProcess(UpsertNCRProcessSteps.target);
                }
            }
        }
    }, [createInitialReport, basicInfoValues.manualNcr, basicInfoValues.ncr, basicInfoValues.description, basicInfoValues.todaySubmission, basicInfoValues.submissionDate, basicInfoValues.reportedBy?.id, basicInfoValues.reportedBy?.firstName, basicInfoValues.reportedBy?.firstname, basicInfoValues.reportedBy?.lastName, basicInfoValues.reportedBy?.lastname, operatorInformation?.id, operatorInformation?.firstName, operatorInformation?.lastName, isMounted, disableNavigatingBetweenSteps, terminateProcess, submittedReport?.statusId, submittedReport?.statuses, submittedReport?.id])

    /**
     * Determines if a ball has been passed over relative to their order in ordering process.
     * @param currentType {string}
     * @param type {string}
     * @return {boolean}
     */
    const passed = useCallback((currentType, type) => {
        switch (currentType) {
            case UpsertNCRProcessSteps.basicInfo:
            default:
                return false;
            case UpsertNCRProcessSteps.questions:
                return type === UpsertNCRProcessSteps.basicInfo;
            case UpsertNCRProcessSteps.target:
                return (
                    type === UpsertNCRProcessSteps.basicInfo ||
                    type === UpsertNCRProcessSteps.questions
                );
        }
    }, []);

    return (
        <Dialog
            disableBackdropClick={currentProcess !== UpsertNCRProcessSteps.basicInfo}
            open={open}
            setOpen={setOpen}
            className={"add-warranty-and-return-dialog"}
            maxWidth={"sm"}
        >
            <TopBarProgressIndicator
                types={UpsertNCRProcessSteps}
                currentType={currentProcess}
                passed={passed}
                className={"dialog-top-section"}
            />
            <Row noGutters className="justify-content-start align-items-start w-100 ">
                {
                    currentProcess === UpsertNCRProcessSteps.basicInfo &&
                    <AddNCRBasicInfoSection
                        submit={submitBasicInfo}
                        basicInfoValues={basicInfoValues}
                        updating={!!report?.id}
                    />
                }
                {
                    currentProcess === UpsertNCRProcessSteps.questions &&
                    <AddNCRQuestionsSection
                        goBack={disableNavigatingBetweenSteps ? undefined : () => setCurrentProcess(UpsertNCRProcessSteps.basicInfo)}
                        submitting={submittingReport}
                        formId={formId}
                        formResponseId={formResponseId}
                        initialRequest={createInitialReport}
                        newResponse={createInitialReport || (!!submittedReport?.statusId && (submittedReport?.statuses?.find(e => e.isCurrentStatus)?.statusId) !== submittedReport?.statusId)}
                        submit={initiateReport}
                    />
                }
                {
                    currentProcess === UpsertNCRProcessSteps.target &&
                    <AddNCRTargetSection
                        goBack={disableNavigatingBetweenSteps ? undefined : () => setCurrentProcess(UpsertNCRProcessSteps.questions)}
                        submit={() => terminateProcess(submittedReport)}
                        close={() => terminateProcess(submittedReport)}
                        lists={targetLists}
                        setLists={setTargetLists}
                        tab={targetsTab}
                        reportId={submittedReport?.id}
                    />
                }
            </Row>
        </Dialog>
    );
};

export default UpsertNCRDialog;
